import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTarry } from './TarryContext';

const TARRY_API = import.meta.env.VITE_TARRY_API || '';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

function getOrCreateSessionId() {
  const STORAGE_KEY = 'tarry_session_id';
  const existing = localStorage.getItem(STORAGE_KEY);
  if (existing) return existing;
  const newId = 'tarry_' + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
  localStorage.setItem(STORAGE_KEY, newId);
  return newId;
}

export default function TarryWidget() {
  const { pageContext, isOpen, setIsOpen, pendingMessage, clearPendingMessage } = useTarry();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [hasGreeted, setHasGreeted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Trigger greeting on first open (or pending message)
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setHasGreeted(true);
      if (pendingMessage) {
        sendToTarry(pendingMessage);
        clearPendingMessage();
      } else {
        sendToTarry('Hello');
      }
    } else if (isOpen && hasGreeted && pendingMessage) {
      // Already greeted but a new intent came in (e.g. user clicked Contact Sales again)
      sendToTarry(pendingMessage);
      clearPendingMessage();
    }
  }, [isOpen, hasGreeted, pendingMessage]);

  const routeLabels: Record<string, string> = {
    '/': 'Homepage',
    '/pricing': 'Pricing',
    '/tapn': 'Tapn',
    '/contact': 'Contact',
    '/blog': 'Blog',
    '/status': 'System Status',
    '/security': 'Security',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
    '/products/payments': 'Payments',
    '/products/subscriptions': 'Subscriptions',
    '/products/connect': 'Connect',
    '/products/radar': 'Radar',
    '/products/billing': 'Billing',
    '/products/terminal': 'Terminal',
    '/developers/docs': 'Documentation',
    '/developers/sdk': 'SDK & Libraries',
    '/developers/webhooks': 'Webhooks',
    '/resources/case-studies': 'Case Studies',
    '/resources/guides': 'Guides',
    '/resources/partners': 'Partners',
  };

  const handleNavigation = useCallback((navAction: string | null) => {
    if (!navAction) return;
    const navMatch = navAction.match(/NAV:([^\s\]]*)/);
    const scrollMatch = navAction.match(/SCROLL:#([^\s\]]*)/);
    if (navMatch) {
      const route = navMatch[1];
      const label = routeLabels[route] || route;
      if (route.startsWith('http')) {
        setMessages(prev => [...prev, { role: 'system', content: `Opening ${label}...` }]);
        setTimeout(() => { window.location.href = route; }, 600);
      } else {
        setMessages(prev => [...prev, { role: 'system', content: `Navigating to ${label}...` }]);
        setTimeout(() => { navigate(route); }, 600);
      }
    }
    if (scrollMatch) {
      const sectionName = scrollMatch[1].replace(/-/g, ' ');
      setMessages(prev => [...prev, { role: 'system', content: `Scrolling to ${sectionName}...` }]);
      setTimeout(() => {
        const el = document.getElementById(scrollMatch[1]);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [navigate]);

  const sendToTarry = async (text: string) => {
    if (text !== 'Hello') {
      setMessages(prev => [...prev, { role: 'user', content: text }]);
    }
    setLoading(true);
    try {
      const res = await fetch(`${TARRY_API}/api/tarry/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, pageContext }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      handleNavigation(data.navigation);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having a moment — give me a second and try again." }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    sendToTarry(input.trim());
    setInput('');
  };

  return (
    <>
      {/* Floating avatar button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 group cursor-pointer"
          aria-label="Chat with Tarry"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-2xl transition-transform duration-300 group-hover:scale-110"
              style={{ boxShadow: '0 8px 32px rgba(37,99,235,0.3)' }}>
              <img src="/tarry-avatar.png" alt="Tarry" className="w-full h-full object-cover" />
            </div>
            {/* Pulse ring */}
            <div className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-30" />
            {/* Online dot */}
            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          </div>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[600px] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
          style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.15)' }}>
          
          {/* Header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100"
            style={{ background: 'linear-gradient(135deg, #1E3A8A, #0D9488)' }}>
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
              <img src="/tarry-avatar.png" alt="Tarry" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-bold text-sm">Tarry</div>
              <div className="text-white/60 text-xs flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                Online — Paygent Sales Rep
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/60 hover:text-white transition-colors cursor-pointer">
              <i className="ri-close-line text-xl w-6 h-6 flex items-center justify-center" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: '400px' }}>
            {messages.map((msg, i) => (
              msg.role === 'system' ? (
                <div key={i} className="flex justify-center">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-xs text-teal-700 font-medium">
                    <i className="ri-compass-3-line w-3.5 h-3.5 flex items-center justify-center animate-spin" style={{ animationDuration: '2s' }} />
                    {msg.content}
                  </div>
                </div>
              ) : (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-800 rounded-bl-md'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              )
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Tarry anything..."
              className="flex-1 px-4 py-2.5 bg-gray-50 rounded-xl text-sm border border-gray-200 focus:outline-none focus:border-blue-400 transition-colors"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-40 cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-2-fill text-sm w-4 h-4 flex items-center justify-center" />
            </button>
          </form>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-50 text-center">
            <span className="text-[10px] text-gray-400">Powered by Wheelcall LLC / Secrettary</span>
          </div>
        </div>
      )}
    </>
  );
}
